import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Drawer, Flex } from '@mantine/core';
import { useEffect } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { Select, TextInput } from 'react-hook-form-mantine';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { PersonRelationType, PersonType, RelatedPersonRes } from '@dealstack/shared/lib/generated';
import { useEditRelatedPersonDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/EditRelatedPersonDrawer/hooks/useEditRelatedPersonDrawer.ts';
import { EDIT_RELATED_PERSON_FORM_FIELDS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/EditRelatedPersonDrawer/utils/types.ts';
import { makeRelationTypeOptions } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/utils/relationTypes.ts';
import { LegalRepresentativeFields } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/components/LegalRepresentativeFields.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { makeOptionWithTestIdByValue } from '@dealstack/frontend/shared/components/Select/withOptionTestId';

// Keyed by value: relation labels are translated and vary by related-person type.
const RelationOption = makeOptionWithTestIdByValue(
	TEST_ELEMENT_IDS.CAP_RELATED_PEOPLE_DRAWER_RELATION_OPTION
);

interface Props {
	relatedPerson?: RelatedPersonRes;
	relationType?: PersonRelationType;
	selectedEntityType?: PersonType;
	opened: boolean;
	onClose: () => void;
}

export const EditRelatedPersonDrawer = ({
	relatedPerson,
	opened,
	onClose,
	relationType,
	selectedEntityType,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const { editPersonRelationForm, onFormSubmit, isLoading, selectedEntityName } =
		useEditRelatedPersonDrawer(onClose, relationType);

	const relatedPersonDisplayName = relatedPerson?.displayName ?? '';
	const naturalPersonName =
		selectedEntityType === PersonType.NATURAL ? selectedEntityName : relatedPersonDisplayName;
	const legalEntityName =
		selectedEntityType === PersonType.NATURAL ? relatedPersonDisplayName : selectedEntityName;

	const relationTypeOptions = makeRelationTypeOptions(tCapManage, selectedEntityType);

	const watchedRelationType = useWatch({
		control: editPersonRelationForm.control,
		name: EDIT_RELATED_PERSON_FORM_FIELDS.relation_type,
	});
	const isPromotingToPiv =
		watchedRelationType === PersonRelationType.PIV && relationType !== PersonRelationType.PIV;

	useEffect(() => {
		editPersonRelationForm.reset({
			...(relationType ? { relation_type: relationType } : {}),
			relation_id: relatedPerson?.relationId,
			[EDIT_RELATED_PERSON_FORM_FIELDS.create_legal_rep]: true,
			[EDIT_RELATED_PERSON_FORM_FIELDS.custom_relation_type]:
				relatedPerson?.customRelationType ?? '',
		});
	}, [relatedPerson, relationType]);

	// A named role describes itself, so the free-text label resets on switch —
	// otherwise a stale role would linger and reappear on switching back.
	const onRelationTypeChange = (value: string | null) => {
		if (value !== PersonRelationType.OTHER) {
			editPersonRelationForm.setValue(
				EDIT_RELATED_PERSON_FORM_FIELDS.custom_relation_type,
				''
			);
		}
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tCapManage('related_people.drawer.edit_title')}
		>
			<FormProvider {...editPersonRelationForm}>
				<Flex direction="column" justify="space-between" h="100%">
					<Flex direction="column" gap="1rem">
						<Select
							name={EDIT_RELATED_PERSON_FORM_FIELDS.relation_type}
							control={editPersonRelationForm.control}
							label={tCapManage('related_people.drawer.form.relation_type_label')}
							placeholder={tCapManage(
								'related_people.drawer.form.relation_type_placeholder'
							)}
							data={relationTypeOptions}
							renderOption={RelationOption}
							data-testid={
								TEST_ELEMENT_IDS.CAP_EDIT_RELATED_PERSON_DRAWER_RELATION_SELECT
							}
							rightSection={<Icon variant="caret-down" size="lg" />}
							styles={{ rightSection: { pointerEvents: 'none' } }}
							searchable
							size="md"
							onChange={onRelationTypeChange}
						/>
						{watchedRelationType === PersonRelationType.OTHER && (
							<TextInput
								name={EDIT_RELATED_PERSON_FORM_FIELDS.custom_relation_type}
								control={editPersonRelationForm.control}
								label={tCapManage(
									'related_people.drawer.form.custom_relation_type_label'
								)}
								placeholder={tCapManage(
									'related_people.drawer.form.custom_relation_type_placeholder'
								)}
								data-testid={
									TEST_ELEMENT_IDS.CAP_EDIT_RELATED_PERSON_DRAWER_CUSTOM_ROLE_INPUT
								}
								size="md"
							/>
						)}
						<Select
							name={EDIT_RELATED_PERSON_FORM_FIELDS.relation_id}
							control={editPersonRelationForm.control}
							label={tCapManage('related_people.drawer.form.person_name_label')}
							placeholder={tCapManage(
								'related_people.drawer.form.person_name_placeholder'
							)}
							data={
								relatedPerson
									? [
											{
												label: relatedPerson.displayName ?? '',
												value: relatedPerson.relationId,
											},
										]
									: []
							}
							disabled={true}
							rightSection={<Icon variant="caret-down" size="lg" />}
							styles={{ rightSection: { pointerEvents: 'none' } }}
							inputWrapperOrder={['label', 'input', 'description']}
							size="md"
						/>
						{isPromotingToPiv && (
							<LegalRepresentativeFields
								fieldName={EDIT_RELATED_PERSON_FORM_FIELDS.create_legal_rep}
								naturalPersonName={naturalPersonName}
								legalEntityName={legalEntityName}
							/>
						)}
					</Flex>

					<Button
						onClick={editPersonRelationForm.handleSubmit(onFormSubmit)}
						disabled={isLoading}
						data-testid={TEST_ELEMENT_IDS.CAP_EDIT_RELATED_PERSON_DRAWER_SUBMIT_BUTTON}
					>
						{tCapManage('related_people.drawer.edit_button')}
					</Button>
				</Flex>
			</FormProvider>
		</Drawer>
	);
};
